import { NO_USER } from './grower-form-values'
import type { UseFormReturn } from 'react-hook-form'
import type { GrowerFormValues } from '@/lib/schemas/grower-schemas'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useUsers } from '@/hooks/use-user-query'
import { m } from '@/lib/paraglide/messages'

type Props = {
    form: UseFormReturn<GrowerFormValues>
    readOnly?: boolean
    /**
     * Show the linked-login-account picker. Off for the household's own screen:
     * a grower login has no users.read, so loading the account list 403s and the
     * field would render blank — and which login is attached is an admin
     * concern, not something the household acts on.
     */
    showUserLink?: boolean
}

/** Section A — Thông tin chung của hộ. */
export function GrowerGeneralSection({
    form,
    readOnly,
    showUserLink = true,
}: Props) {
    const disabled = !!readOnly
    // Hooks cannot be conditional, so the query is disabled rather than skipped.
    const { data: usersData } = useUsers(
        { per_page: 100 },
        { enabled: showUserLink },
    )
    const users = usersData?.data ?? []
    return (
        <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
            <div className="space-y-1.5">
                <Label>
                    {m.grower_name()}{' '}
                    <span className="text-destructive">*</span>
                </Label>
                <Input
                    disabled={disabled}
                    {...form.register('name')}
                    data-testid="grower-name"
                />
                {form.formState.errors.name && (
                    <p className="text-xs text-destructive">
                        {form.formState.errors.name.message}
                    </p>
                )}
            </div>
            <div className="space-y-1.5">
                <Label>{m.grower_nickname()}</Label>
                <Input
                    disabled={disabled}
                    {...form.register('nickname')}
                    data-testid="grower-nickname"
                />
                <p className="text-xs text-muted-foreground">
                    {m.grower_nickname_hint()}
                </p>
            </div>

            <div className="space-y-1.5">
                <Label>
                    {m.grower_phone()}{' '}
                    <span className="text-destructive">*</span>
                </Label>
                <Input
                    disabled={disabled}
                    {...form.register('phone')}
                    data-testid="grower-phone"
                />
            </div>

            <div className="space-y-1.5">
                <Label>
                    {m.grower_national_id()}{' '}
                    <span className="text-destructive">*</span>
                </Label>
                <Input disabled={disabled} {...form.register('national_id')} />
            </div>
            <div className="space-y-1.5">
                <Label>{m.grower_birthday()}</Label>
                <Input
                    type="date"
                    disabled={disabled}
                    {...form.register('birthday')}
                />
            </div>

            <div className="space-y-1.5">
                <Label>{m.grower_national_id_issued_date()}</Label>
                <Input
                    type="date"
                    disabled={disabled}
                    {...form.register('national_id_issued_date')}
                />
            </div>
            <div className="space-y-1.5">
                <Label>{m.grower_national_id_issued_place()}</Label>
                <Input
                    disabled={disabled}
                    {...form.register('national_id_issued_place')}
                />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
                <Label>{m.grower_address()}</Label>
                <Textarea
                    rows={2}
                    disabled={disabled}
                    {...form.register('address')}
                />
            </div>

            {showUserLink && (
                <div className="space-y-1.5">
                    <Label>{m.grower_user_link()}</Label>
                    <Select
                        disabled={disabled}
                        value={form.watch('user_id') || NO_USER}
                        onValueChange={(v) => form.setValue('user_id', v)}
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={NO_USER}>
                                {m.grower_user_link_none()}
                            </SelectItem>
                            {users.map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                    {u.full_name} ({u.username})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}
        </div>
    )
}
